<script lang="ts">
  import { flip } from 'svelte/animate'
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { normalizeError } from '#app/lib/error'
  import { onChange } from '#app/lib/svelte/svelte'
  import InfiniteScroll from '#components/infinite_scroll.svelte'
  import EntityAutocompleteSelector, { type EntityAutocompleteSuggestion } from '#entities/components/entity_autocomplete_selector.svelte'
  import Spinner from '#general/components/spinner.svelte'
  import { searchTypesByListingType, i18nSearchPlaceholderKeys } from '#listings/lib/entities_typing'
  import { addElement, assignEntitiesToElements, type SerializedListing } from '#listings/lib/listings'
  import type { ListingElement as ListingElementT } from '#server/types/element'
  import type { ListingId } from '#server/types/listing'
  import { i18n, I18n } from '#user/lib/i18n'
  import type { SerializedUser } from '#users/lib/users'
  import ListingElement from './listing_element.svelte'

  let {
    elements = $bindable([]),
    listing,
    creator,
    isEditable,
  }: {
    elements?: ListingElementT[]
    listing: SerializedListing
    creator: SerializedUser
    isEditable: boolean
  } = $props()

  let flash: FlashState | undefined = $state()
  let inputValue = $state('')
  let showSuggestions = $state(false)

  let paginatedElements = $state([])
  const paginationSize = 15
  let offset = $state(0)
  let fetching
  const searchTypes = $derived(searchTypesByListingType[listing.type])
  const i18nSearchPlaceholder = $derived(i18nSearchPlaceholderKeys[listing.type])

  let addingAnElement: Promise<unknown> | undefined = $state()

  async function addUriAsElement (entity: EntityAutocompleteSuggestion) {
    flash = undefined
    inputValue = ''
    showSuggestions = false
    addingAnElement = _addUriAsElement(listing._id, entity)
  }

  async function _addUriAsElement (listingId: ListingId, entity: EntityAutocompleteSuggestion) {
    try {
      const { createdElements, alreadyInList } = await addElement(listingId, entity.uri)
      if (isNonEmptyArray(alreadyInList)) {
        return flash = {
          type: 'info',
          message: i18n('This element is already in the list'),
        }
      }
      // Re fetch entities with fitting attributes.
      await assignEntitiesToElements(createdElements)
      elements = [ ...elements, ...createdElements ]
      fetchMore(true)
      return flash = {
        type: 'success',
        message: i18n('Added to the list'),
      }
    } catch (err) {
      flash = normalizeError(err)
    }
  }

  const hasMore = $derived(elements.length >= offset)

  async function fetchMore (isReset = false) {
    fetching = true
    if (isReset) offset = 0
    const nextBatchElements = elements.slice(offset, offset + paginationSize)
    await assignEntitiesToElements(nextBatchElements)
    if (isNonEmptyArray(nextBatchElements)) {
      offset += paginationSize
      paginatedElements = isReset ? nextBatchElements : [ ...paginatedElements, ...nextBatchElements ]
    }
    fetching = false
  }

  const waitingForEntities = fetchMore()

  async function keepScrolling () {
    if (fetching || hasMore === false) return false
    await fetchMore()
    return true
  }

  function refreshElements () {
    if (fetching) return
    paginatedElements = elements.slice(0, paginatedElements.length)
  }

  $effect(() => {
    onChange(elements, refreshElements)
  })
</script>

{#await waitingForEntities}
  <Spinner center={true} />
{:then}
  <section class="entities-listing-section">
    {#if isEditable}
      <div class="entities-selector">
        <label for={inputValue}>
          {i18n('Add to this list')}
          <EntityAutocompleteSelector
            {searchTypes}
            placeholder={i18n(i18nSearchPlaceholder)}
            autofocus={false}
            bind:currentEntityLabel={inputValue}
            bind:showSuggestions
            onSelect={addUriAsElement}
            onFocus={() => { flash = undefined }}
          />
        </label>
        <Flash bind:state={flash} />
      </div>
    {/if}

    <InfiniteScroll {keepScrolling} showSpinner={true}>
      <ul class="listing-elements">
        {#await addingAnElement}
          <li class="loading">{I18n('loading')}<Spinner /></li>
        {/await}
        {#each paginatedElements as element (element.uri)}
          <li
            class="listing-element"
            animate:flip={{ duration: 300 }}
          >
            <ListingElement
              {isEditable}
              {element}
              {listing}
              {creator}
              bind:elements
              isCreatorMainUser={isEditable}
              bind:autocompleteFlash={flash}
            />
          </li>
        {:else}
          <li>
            <p class="empty">
              {i18n('There is nothing here')}
            </p>
          </li>
        {/each}
      </ul>
    </InfiniteScroll>
  </section>
{/await}

<style lang="scss">
  @import "general/scss/utils";
  .entities-listing-section{
    width: 100%;
    padding: 0 1em;
    :global(.infinite-scroll-wrapper){
      width: 100%;
    }
  }
  label{
    cursor: auto;
  }
  .listing-elements{
    @include display-flex(column, center);
    @include radius;
    margin: 1em 0;
    overflow: hidden;
  }
  li{
    @include display-flex(row, center);
    @include bg-hover(white);
    width: 100%;
  }
  .listing-element{
    padding-inline-end: 0.5em;
    border-block-end: 1px solid $light-grey;
  }
  .empty{
    color: $grey;
    margin: auto;
    padding: 0.5em;
    opacity: 0.8;
    font-style: italic;
  }
  /* Large screens */
  @media screen and (width > 40em){
    .entities-listing-section{
      width: 40em;
    }
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .entities-selector{
      padding: 0 0.5em;
    }
    .entities-listing-section{
      padding: 0;
    }
  }
</style>
